import { useCallback, useEffect, useReducer, useRef } from "react";
import type { SwipeVariantId, TrainingItemOut } from "../api/types";
import type { DrillOutcome } from "./useDrillSession";

// ---------------------------------------------------------------------------
// Tinder-style swipe drill state machine.
//
// Each card runs through two phases:
//   swiping  — the card is on top, the per-card countdown ticks. The user
//              swipes left/right (or the clock hits zero = fail).
//   feedback — brief pause showing correct/wrong + the best move; the host
//              plays the throw animation. Then the machine advances to the
//              next card or completes.
//
// Cards carry their moves in `item.choices`:
//   judge — exactly 1 option; right-swipe is correct iff its kind is "best".
//   duel  — exactly 2 options; index 0 is the LEFT side, index 1 the RIGHT.
//
// Like the other drill hooks this is platform-pure: the host supplies the
// clock via `nowMs`, so the reducer is deterministic + unit-testable. It
// reuses `DrillOutcome` so markSolved/complete hooks plug in unchanged
// (tries_used is always 1 — swipes are single-shot).
// ---------------------------------------------------------------------------

export type SwipePhase = "swiping" | "feedback";
export type SwipeDirection = "left" | "right";

export interface SwipeDrillState {
  index: number;
  items: TrainingItemOut[];
  variant: SwipeVariantId;
  phase: SwipePhase;
  /** Seconds left to swipe; null when the item carries no clock. */
  remainingSec: number | null;
  /** The direction the user swiped (null on timeout / before a swipe). */
  swiped: SwipeDirection | null;
  /** Whether the resolved card was answered correctly (null until resolved). */
  correct: boolean | null;
  /** True when the card resolved because the clock ran out. */
  timedOut: boolean;
  /** Wall-clock (ms) when the card's swiping phase began; 0 until INIT. */
  swipingStartedAt: number;
  /** Whole session finished (advanced past the last card). */
  completed: boolean;
}

export type SwipeAction =
  | { type: "INIT"; items: TrainingItemOut[]; variant: SwipeVariantId; nowMs: number }
  | { type: "SWIPE"; dir: SwipeDirection; nowMs: number }
  | { type: "TICK"; nowMs: number }
  | { type: "ADVANCE"; nowMs: number }
  | { type: "COMPLETE" };

export interface SwipeReducerResult {
  state: SwipeDrillState;
  /** Emitted once when a card resolves (a swipe or a timeout). */
  outcome: DrillOutcome | null;
}

const NO_OUTCOME: SwipeReducerResult["outcome"] = null;

/** The direction that correctly answers `item` under `variant`. */
export function correctDirection(
  item: TrainingItemOut | undefined,
  variant: SwipeVariantId,
): SwipeDirection {
  const first = item?.choices?.[0];
  if (variant === "judge") return first?.kind === "best" ? "right" : "left";
  // duel: index 0 = left side, index 1 = right side; one of them is best.
  return first?.kind === "best" ? "left" : "right";
}

export function isCorrectSwipe(
  item: TrainingItemOut | undefined,
  variant: SwipeVariantId,
  dir: SwipeDirection,
): boolean {
  return dir === correctDirection(item, variant);
}

/** The UCI to log as the wrong move for an incorrect swipe. */
function wrongMoveUci(
  item: TrainingItemOut | undefined,
  variant: SwipeVariantId,
  dir: SwipeDirection,
): string | undefined {
  const choices = item?.choices ?? [];
  // judge: the misjudged shown move; duel: the side the user picked.
  if (variant === "judge") return choices[0]?.uci;
  return (dir === "left" ? choices[0] : choices[1])?.uci;
}

function freshCardState(
  items: TrainingItemOut[],
  variant: SwipeVariantId,
  index: number,
  nowMs: number,
  carry: Pick<SwipeDrillState, "completed">,
): SwipeDrillState {
  return {
    index,
    items,
    variant,
    phase: "swiping",
    remainingSec: items[index]?.time_limit_seconds ?? null,
    swiped: null,
    correct: null,
    timedOut: false,
    swipingStartedAt: nowMs,
    completed: carry.completed,
  };
}

export function initSwipeState(
  items: TrainingItemOut[],
  variant: SwipeVariantId,
  nowMs = 0,
  index = 0,
): SwipeDrillState {
  return freshCardState(items, variant, index, nowMs, { completed: false });
}

function buildOutcome(
  state: SwipeDrillState,
  nowMs: number,
  opts: { solved: boolean; timedOut: boolean; wrongUci?: string },
): DrillOutcome {
  return {
    solved: opts.solved,
    tries_used: 1,
    ms_taken: state.swipingStartedAt ? nowMs - state.swipingStartedAt : 0,
    wrong_moves: !opts.solved && opts.wrongUci ? [opts.wrongUci] : [],
    timed_out: opts.timedOut,
  };
}

export function swipeReducer(state: SwipeDrillState, action: SwipeAction): SwipeReducerResult {
  switch (action.type) {
    case "INIT":
      return {
        state: initSwipeState(action.items, action.variant, action.nowMs, 0),
        outcome: NO_OUTCOME,
      };

    case "SWIPE": {
      if (state.phase !== "swiping") return { state, outcome: NO_OUTCOME };
      const item = state.items[state.index];
      const solved = isCorrectSwipe(item, state.variant, action.dir);
      return {
        state: { ...state, phase: "feedback", swiped: action.dir, correct: solved },
        outcome: buildOutcome(state, action.nowMs, {
          solved,
          timedOut: false,
          wrongUci: wrongMoveUci(item, state.variant, action.dir),
        }),
      };
    }

    case "TICK": {
      if (state.phase !== "swiping" || state.remainingSec == null) {
        return { state, outcome: NO_OUTCOME };
      }
      const remainingSec = state.remainingSec - 1;
      if (remainingSec <= 0) {
        // Out of time — resolve as a failed (timed-out) card.
        return {
          state: { ...state, phase: "feedback", remainingSec: 0, correct: false, timedOut: true },
          outcome: buildOutcome(state, action.nowMs, { solved: false, timedOut: true }),
        };
      }
      return { state: { ...state, remainingSec }, outcome: NO_OUTCOME };
    }

    case "ADVANCE": {
      const nextIndex = state.index + 1;
      if (nextIndex < state.items.length) {
        return {
          state: freshCardState(state.items, state.variant, nextIndex, action.nowMs, {
            completed: state.completed,
          }),
          outcome: NO_OUTCOME,
        };
      }
      return { state, outcome: NO_OUTCOME };
    }

    case "COMPLETE":
      return { state: { ...state, completed: true }, outcome: NO_OUTCOME };

    default:
      return { state, outcome: NO_OUTCOME };
  }
}

// ---------------------------------------------------------------------------
// Shrinking-stack visual. Count decks shrink 1:1; time decks show a fixed
// virtual stack that stops shrinking near the end (the true count is fuzzy).
// ---------------------------------------------------------------------------

export const SWIPE_VISUAL_DECK = 100;
export const SWIPE_VISUAL_FLOOR = 10;

export function visualCardsLeft(index: number, total: number, timeBased: boolean): number {
  if (!timeBased) return Math.max(0, total - index);
  const start = Math.min(SWIPE_VISUAL_DECK, total);
  return Math.max(Math.min(SWIPE_VISUAL_FLOOR, start), start - index);
}

// ---------------------------------------------------------------------------
// React wrapper. Drives the per-card countdown and the feedback auto-advance;
// fires host callbacks on resolve + complete.
// ---------------------------------------------------------------------------

export interface UseSwipeDrillOptions {
  items: TrainingItemOut[];
  variant: SwipeVariantId;
  /** ms the feedback flash shows before auto-advancing (default 1100). */
  feedbackMs?: number;
  /** Fired once per card when it resolves (swipe or timeout). */
  onSolveItem?: (item: TrainingItemOut, outcome: DrillOutcome) => void;
  /** Fired once when the session runs out of cards. */
  onComplete?: () => void;
}

export interface UseSwipeDrill {
  current: TrainingItemOut | undefined;
  index: number;
  total: number;
  phase: SwipePhase;
  remainingSec: number | null;
  swiped: SwipeDirection | null;
  correct: boolean | null;
  timedOut: boolean;
  completed: boolean;
  /** True once the card has resolved (feedback phase). */
  resolved: boolean;
  /** Submit a swipe (no-op outside the swiping phase). */
  swipe: (dir: SwipeDirection) => void;
}

const now = () => Date.now();

function itemsKey(items: TrainingItemOut[]): string {
  return items.map((it) => it.id).join("|");
}

export function useSwipeDrill(opts: UseSwipeDrillOptions): UseSwipeDrill {
  const { items, variant, feedbackMs = 1100 } = opts;

  const [{ state }, rawDispatch] = useReducer(
    (acc: SwipeReducerResult, action: SwipeAction) => swipeReducer(acc.state, action),
    items,
    (init) => ({ state: initSwipeState(init, variant, now(), 0), outcome: NO_OUTCOME }),
  );

  const cbRef = useRef(opts);
  cbRef.current = opts;
  const stateRef = useRef(state);
  stateRef.current = state;

  const dispatch = useCallback((action: SwipeAction) => rawDispatch(action), []);

  // Emit the resolve callback exactly once per card by re-running the pure
  // reducer to read the outcome side-channel (useReducer can't surface it).
  const resolvedIndexRef = useRef(-1);
  const emit = useCallback(
    (action: SwipeAction) => {
      const result = swipeReducer(stateRef.current, action);
      if (result.outcome && resolvedIndexRef.current !== stateRef.current.index) {
        resolvedIndexRef.current = stateRef.current.index;
        const item = stateRef.current.items[stateRef.current.index];
        if (item) cbRef.current.onSolveItem?.(item, result.outcome);
      }
      dispatch(action);
    },
    [dispatch],
  );

  // Re-init when a different item list arrives (e.g. the session query resolves
  // after mount). Mirrors useChoiceDrill's guard so progress isn't clobbered
  // when markSolved invalidations refetch the same session.
  const itemsKeyRef = useRef(itemsKey(items));
  useEffect(() => {
    const key = itemsKey(items);
    if (key === itemsKeyRef.current && state.items.length > 0) return;
    itemsKeyRef.current = key;
    resolvedIndexRef.current = -1;
    dispatch({ type: "INIT", items, variant, nowMs: now() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, dispatch]);

  // Swiping: per-second countdown. The reducer no-ops outside the phase.
  useEffect(() => {
    if (state.phase !== "swiping" || state.remainingSec == null) return;
    const id = setInterval(() => emit({ type: "TICK", nowMs: now() }), 1000);
    return () => clearInterval(id);
  }, [state.phase, state.remainingSec, emit]);

  // Feedback: hold the result, then advance (or complete at the end).
  useEffect(() => {
    if (state.phase !== "feedback") return;
    const id = setTimeout(() => {
      const s = stateRef.current;
      resolvedIndexRef.current = -1;
      if (s.index + 1 < s.items.length) {
        dispatch({ type: "ADVANCE", nowMs: now() });
      } else {
        dispatch({ type: "COMPLETE" });
        cbRef.current.onComplete?.();
      }
    }, feedbackMs);
    return () => clearTimeout(id);
  }, [state.phase, state.index, feedbackMs, dispatch]);

  const swipe = useCallback(
    (dir: SwipeDirection) => emit({ type: "SWIPE", dir, nowMs: now() }),
    [emit],
  );

  const item = state.items[state.index];
  return {
    current: item,
    index: state.index,
    total: state.items.length,
    phase: state.phase,
    remainingSec: state.remainingSec,
    swiped: state.swiped,
    correct: state.correct,
    timedOut: state.timedOut,
    completed: state.completed,
    resolved: state.phase === "feedback",
    swipe,
  };
}
